import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
import { useStories } from "../hooks/useStories";
import { useDeleteStory, useMarkStoryViewed, useSendStoryReply, Story as StoryType } from "../lib/api";
import { convertToShamsiRelative } from "../utils/dateUtils";
import { getAuthUser } from "../utils/cookieUtils";
import Story from "./Story";
import CreateStoryButton from "./CreateStoryButton";
import CreateStoryModal from "./CreateStoryModal";

type StoriesProps = {};

const Stories: React.FC<StoriesProps> = () => {
  const { t } = useTranslation();
  const [selectedUserIndex, setSelectedUserIndex] = useState<number | null>(null);
  const [selectedStoryIndex, setSelectedStoryIndex] = useState<number>(0);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [replyInput, setReplyInput] = useState("");
  const markedSeenRef = useRef<Set<string>>(new Set());

  const navigate = useNavigate();
  const { data: stories = [], isLoading, isError } = useStories();
  const deleteStoryMutation = useDeleteStory();
  const sendStoryReplyMutation = useSendStoryReply();
  const markStoryViewedMutation = useMarkStoryViewed();
  const currentUser = getAuthUser();

  // Group stories by user (similar to Instagram)
  const groupedStories = useMemo(() => {
    if (!stories || stories.length === 0) return [];
    
    const groups: { [userId: number]: StoryType[] } = {};
    
    stories.forEach((story: StoryType) => {
      // Skip stories with invalid data
      if (!story || !story.userId) return;
      
      if (!groups[story.userId]) {
        groups[story.userId] = [];
      }
      groups[story.userId].push(story);
    });
    
    // Convert to array and sort by latest story
    return Object.values(groups)
      .map(userStories => {
        const firstStory = userStories[0];
        if (!firstStory) return null;
        
        return {
          user: firstStory.user || { 
            id: firstStory.userId, 
            username: `user_${firstStory.userId}`, 
            name: `User ${firstStory.userId}`,
            image: '/default-avatar.svg'
          },
          stories: userStories
            .filter(story => story && story.createdAt) // Filter out invalid stories
            .sort((a, b) => {
              if (!a || !b) return 0;
              return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            })
        };
      })
      .filter(group => group !== null && group.stories.length > 0) // Remove null groups
      .sort((a, b) => {
        if (!a || !b) return 0;
        return new Date(b.stories[0].createdAt).getTime() - new Date(a.stories[0].createdAt).getTime();
      });
  }, [stories]);



  const handleStoryClick = useCallback((userIndex: number) => {
    setSelectedUserIndex(userIndex);
    setSelectedStoryIndex(0);
    setShowModal(true);
  }, []);

  const closeModal = () => {
    setShowModal(false);
    setSelectedUserIndex(null);
    setSelectedStoryIndex(0);
    markedSeenRef.current.clear();
  };

  const handleCreateStory = () => {
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
  };

  const handleDeleteStory = async (storyId: string) => {
    if (!confirm('آیا مطمئن هستید که می‌خواهید این استوری را حذف کنید؟')) {
      return;
    }

    try {
      await deleteStoryMutation.mutateAsync(storyId);
      
      if (currentStory && currentStory.id === storyId) {
        if (currentUserStories.length > 1) {
          if (selectedStoryIndex < currentUserStories.length - 1) {
            setSelectedStoryIndex(selectedStoryIndex + 1);
          } else if (selectedStoryIndex > 0) {
            setSelectedStoryIndex(selectedStoryIndex - 1);
          } else {
            closeModal();
          }
        } else {
          closeModal();
        }
      }
    } catch (error) {
      console.error('Error deleting story:', error);
      alert('خطا در حذف استوری. لطفاً دوباره تلاش کنید.');
    }
  };

  const currentUserStories = selectedUserIndex !== null ? groupedStories[selectedUserIndex]?.stories || [] : [];
  
  const hasPrevStory = selectedStoryIndex > 0;
  const hasNextStory = selectedStoryIndex < currentUserStories.length - 1;
  const hasPrevUser = selectedUserIndex !== null && selectedUserIndex > 0;
  const hasNextUser = selectedUserIndex !== null && selectedUserIndex < groupedStories.length - 1;

  const goPrev = useCallback(() => {
    if (hasPrevStory) {
      setSelectedStoryIndex(selectedStoryIndex - 1);
    } else if (hasPrevUser && selectedUserIndex !== null) {
      setSelectedUserIndex(selectedUserIndex - 1);
      const prevUserStories = groupedStories[selectedUserIndex - 1];
      if (prevUserStories) {
        setSelectedStoryIndex(prevUserStories.stories.length - 1);
      }
    }
  }, [hasPrevStory, hasPrevUser, selectedStoryIndex, selectedUserIndex, groupedStories]);

  const goNext = useCallback(() => {
    if (hasNextStory) {
      setSelectedStoryIndex(selectedStoryIndex + 1);
    } else if (hasNextUser && selectedUserIndex !== null) {
      setSelectedUserIndex(selectedUserIndex + 1);
      setSelectedStoryIndex(0);
    }
  }, [hasNextStory, hasNextUser, selectedStoryIndex, selectedUserIndex]);

  useEffect(() => {
    if (!showModal) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      } else if (e.key === "Escape") {
        e.preventDefault();
        closeModal();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showModal, goPrev, goNext]);

  // Persist "seen" on backend when user views a story — once per story id to avoid duplicate requests
  useEffect(() => {
    if (!showModal || selectedUserIndex === null) return;
    const list = groupedStories[selectedUserIndex]?.stories ?? [];
    const story = list[selectedStoryIndex];
    const storyId = story?.id ? String(story.id) : null;
    if (storyId && !markedSeenRef.current.has(storyId)) {
      markedSeenRef.current.add(storyId);
      markStoryViewedMutation.mutate(storyId);
    }
  }, [showModal, selectedUserIndex, selectedStoryIndex, groupedStories, markStoryViewedMutation]);

  const handleSendReply = useCallback(
    (otherUserId: number, storyId?: string | number) => {
      const body = replyInput.trim();
      if (!body || otherUserId === currentUser?.id) return;
      const numericStoryId = storyId != null ? Number(storyId) : undefined;
      sendStoryReplyMutation.mutate(
        { otherUserId, body, storyId: numericStoryId },
        { onSuccess: () => setReplyInput("") }
      );
    },
    [replyInput, currentUser?.id, sendStoryReplyMutation]
  );

  if (isLoading) {
    return (
      <div className="flex items-start gap-4 p-4 bg-white dark:bg-gray-900 mt-2 overflow-x-auto">
        {currentUser && (
          <div className="flex flex-col items-center space-y-2 flex-shrink-0">
            <div className="bg-gradient-to-tr from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 rounded-full p-[3px] animate-pulse">
              <div className="bg-white dark:bg-gray-800 rounded-full p-[3px]">
                <div className="w-14 h-14 rounded-full bg-gray-200 dark:bg-gray-600 animate-pulse"></div>
              </div>
            </div>
            <div className="w-14 h-3 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
          </div>
        )}
        {[...Array(4)].map((_, index) => (
          <div key={index} className="flex flex-col items-center space-y-2 flex-shrink-0">
            <div className="bg-gradient-to-tr from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 rounded-full p-[3px] animate-pulse">
              <div className="bg-white dark:bg-gray-800 rounded-full p-[3px]">
                <div className="w-14 h-14 rounded-full bg-gray-200 dark:bg-gray-600 animate-pulse"></div>
              </div>
            </div>
            <div className="w-14 h-3 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
          </div>
        ))}
      </div>
    );
  }

  // If there's an error, don't render the stories section at all
  if (isError) {
    return null;
  }

  const currentStory = selectedUserIndex !== null && currentUserStories.length > 0 && selectedStoryIndex < currentUserStories.length
    ? currentUserStories[selectedStoryIndex] 
    : null;

  const isVideo = (url: string) => /\.(mp4|webm|ogg)$/i.test(url);
  return (
    (groupedStories.length>0 || currentUser) ?
      <div className="flex items-start gap-4 p-3 bg-white dark:bg-gray-900 mt-2 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">

        {currentUser && (
            <CreateStoryButton onClick={handleCreateStory} />
        )}
        
        {/* User stories */}
        {groupedStories.map((userGroup, index) => {
          if (!userGroup) return null;
          return (
              <Story
                key={`user-${userGroup.user.id}`}
                img={userGroup.stories[0].media}
                username={userGroup.user.username}
                fullname={userGroup.user.name}
                userImage={userGroup.user.image}
                onSelectUserIndex={handleStoryClick}
                userIndex={index}
                seen={userGroup.stories.some((s) => s.seen)}
              />
          );
        })}

      {showModal && currentStory && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={t('story.viewer')}
          onClick={(e) => {
            // Close on backdrop click (but not on content click)
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          {/* Modal: max dimensions, flex column for clear hierarchy */}
          <div 
            className="relative flex h-full w-full max-h-[100dvh] flex-col md:max-h-[90vh] md:max-w-[min(100vw,28rem)]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Progress bars: top strip, no overlap with close button */}
            {currentUserStories.length > 1 && (
              <div className="absolute top-0 left-0 right-0 z-20 flex gap-1 px-3 pt-3">
                {currentUserStories.map((_, index) => (
                  <div
                    key={index}
                    className="h-0.5 flex-1 overflow-hidden rounded-full bg-white/30"
                  >
                    <div
                      className="h-full rounded-full bg-white transition-[width] duration-300 ease-linear"
                      style={{
                        width:
                          index < selectedStoryIndex
                            ? "100%"
                            : index === selectedStoryIndex
                              ? "100%"
                              : "0%",
                      }}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="absolute top-3 start-3 end-3 z-20 flex items-center justify-between gap-2">
              {currentStory.user?.id === currentUser?.id ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteStory(currentStory.id);
                  }}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white transition hover:bg-red-500/80 focus:outline-none focus:ring-2 focus:ring-white/50"
                  aria-label={t('story.deleteStory')}
                  title={t('story.deleteStory')}
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              ) : (
                <div />
              )}
              <button
                type="button"
                onClick={closeModal}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white transition hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-white/50"
                aria-label={t('story.close')}
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {(hasPrevStory || hasPrevUser) && (
              <div
                role="button"
                tabIndex={0}
                onClick={goPrev}
                onKeyDown={(e) => e.key === "Enter" && goPrev()}
                className="absolute top-0 bottom-0 start-0 z-10 w-1/3 cursor-pointer"
                aria-label={t('story.previous')}
              />
            )}
            {(hasNextStory || hasNextUser) && (
              <div
                role="button"
                tabIndex={0}
                onClick={goNext}
                onKeyDown={(e) => e.key === "Enter" && goNext()}
                className="absolute top-0 bottom-0 end-0 z-10 w-1/3 cursor-pointer"
                aria-label={t('story.next')}
              />
            )}

            <div className="flex min-h-0 flex-1 items-center justify-center p-0 md:p-2 relative">
              {isVideo(currentStory.media) ? (
                <video
                  key={currentStory.id}
                  src={currentStory.media}
                  className="max-h-[85vh] max-w-full object-contain transition-opacity duration-200"
                  autoPlay
                  muted
                  loop
                  controls
                  playsInline
                />
              ) : (
                <img
                  key={currentStory.id}
                  src={currentStory.media}
                  alt={`Story by ${currentStory.user?.name || currentStory.user?.username}`}
                  className="max-h-[85vh] max-w-full select-none object-contain transition-opacity duration-200"
                  draggable={false}
                  loading="eager"
                />
              )}
              {currentStory.postId != null && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeModal();
                    navigate(`/post/${currentStory.postId}`);
                  }}
                  className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20 rounded-full bg-white/90 dark:bg-gray-800/90 text-gray-900 dark:text-gray-100 px-5 py-2.5 text-sm font-medium shadow-lg hover:bg-white dark:hover:bg-gray-800 transition-colors"
                  aria-label={t('story.viewPost')}
                >
                  {t('story.viewPost')}
                </button>
              )}
            </div>

            <div className="absolute top-4 left-16 end-4 z-10 flex justify-center md:top-15">
              <div className="flex items-center gap-0 rounded-full bg-black/50 px-3 py-2">
                <div className="flex items-center gap-0 text-white">
                  <span className="text-[11px] opacity-90">{convertToShamsiRelative(currentStory.createdAt)}</span>
                </div>
              </div>
            </div>

            {currentStory.user?.id !== currentUser?.id && (
              <div
                className="absolute bottom-0 left-0 right-0 z-20 flex items-center gap-2 bg-black/90 px-3 py-3 pb-[env(safe-area-inset-bottom)] md:px-4"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <input
                  type="text"
                  value={replyInput}
                  onChange={(e) => setReplyInput(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "ArrowLeft" || e.key === "ArrowRight") return;
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (currentStory?.user?.id) handleSendReply(currentStory.user.id, currentStory.id);
                    }
                  }}
                  placeholder="پیام به این استوری..."
                  className="flex-1 min-w-0 rounded-xl border border-white/10 bg-white/10 px-4 text-xs text-white placeholder-white/60 focus:border-white/50 focus:outline-none focus:ring-1 focus:ring-white/30"
                  aria-label={t('story.replyToStory')}
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (currentStory?.user?.id) handleSendReply(currentStory.user.id, currentStory.id);
                  }}
                  disabled={!replyInput.trim() || sendStoryReplyMutation.isPending}
                  className="flex-shrink-0 rounded-xl bg-white/20 px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50 hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/50"
                  aria-label={t('story.sendReply')}
                >
                  {sendStoryReplyMutation.isPending ? "..." : t('post.sendComment')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <CreateStoryModal
        isOpen={showCreateModal}
        onClose={closeCreateModal}
      />
      </div>
    : ''
  );
};

export default Stories;
